import styled from 'helpers/styles';
import PropTypes from 'prop-types';
import ComponentStyles from 'styles/mixins/Component.styles';
import FormControl from 'components/atoms/Controls/FormControl/FormControl';

const StyledTextField = styled(FormControl)`
  &.TextField-ellipsis {
    .MuiInputBase-root {
      .MuiInputBase-input {
        text-overflow: ellipsis;
      }
    }
  }
  
  &.FormControl-fullHeight {
    .TextField-readOnly {
      .EditorReadOnly, .Markdown {
        height: 100%;
      }
    }
  }

  &.FormControl-fullWidth {
    .TextField-readOnly {
      .EditorReadOnly, .Markdown {
        width: 100%;
      }
    }

    .TextField-container {
      overflow-x: hidden;
    }
  }
  
  &.TextField-multiline {
    &:not(.TextField-markdown, .TextField-richText) {
      .TextField-readOnly {
        white-space: pre-line;
        word-wrap: break-word;
      }
    }
  }

  &.TextField {
    .MuiInputBase-root {
      font-size: ${props => props.theme.fontSize(16)};
      line-height: ${props => props.theme.lineHeight(`${24 / 16}f`)};
      input::placeholder, textarea::placeholder {
        font-size: ${props => props.theme.fontSize(16)};
        line-height: ${props => props.theme.lineHeight(`${24 / 16}f`)};
      }
    }
    
    .TextField-readOnly {
      display: flex;

      * {
        user-select: auto;
        -webkit-user-select: auto;
      }

      &:has(.placeholder) {
        color: ${props => props.theme.property('palette.text.disabled')};
      }

      .MuiInputAdornment-root {
        margin: 0;
        height: unset;
        min-height: unset;
        max-height: unset;
        color: unset;
        font: inherit;

        > * {
          font: inherit;
          color: unset;
        }
      }

      .MuiInputAdornment-positionStart {
        margin-right: ${props => props.theme.spacing(0.5)};
      }

      .MuiInputAdornment-positionEnd {
        margin-left: ${props => props.theme.spacing(0.5)};
      }
    }

    .MuiInputAdornment-root {
      .Link {
        text-decoration: none !important;

        &:has(.Icon) {
          line-height: 0;
        }
      }

      > .Icon {
        font-size: ${props => props.theme.fontSize(20)};
        color: ${props => props.theme.property('palette.action.active')};
      }
      
      .IconButton {
        font-size: ${props => props.theme.fontSize(16)};
        padding: ${props => props.theme.layout(2)};

        > .Icon {
          font-size: ${props => props.theme.fontSize(20)};
          color: ${props => props.theme.property('palette.action.active')};
        }
      }
    }

    .MuiInputAdornment-positionStart {
      margin-left: ${props => props.theme.layout(-3)};

      .IconButton {
        margin-left: ${props => props.theme.layout(-2)};
      }
    }

    .MuiInputAdornment-positionEnd {
      margin-right: ${props => props.theme.layout(-3)};

      .IconButton {
        margin-right: ${props => props.theme.layout(-2)};
      }
    }

    ${props => props.theme.state2Selector('TextField', null, 'disabled')} {
      .MuiInputAdornment-root {
        color: ${props => props.theme.property('palette.action.disabled')};

        * {
          color: ${props => props.theme.property('palette.action.disabled')};
        }
      }
    }

    &:not(.TextField-multiline) {
      .MuiInputBase-root {
        .MuiInputBase-input {
          &:placeholder-shown {
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
          }
        }
      }
    }
    
    &.TextField-multiline {
      .MuiInputBase-root {
        padding: ${props => props.theme.layout(8)} ${props => props.theme.layout(14)};

        pointer-events: none;
        > *:not(fieldset) {
          pointer-events: all;
        }
        
        > .EditorInput, > .MentionsInput, > textarea {
          margin: ${props => props.theme.layout(-8)} ${props => props.theme.layout(-14)};
          width: calc(100% + (${props => props.theme.layout(14)} * 2));
          height: calc(100% + (${props => props.theme.layout(8)} * 2));
        }
        
        > .EditorInput .ProseMirror, > .MentionsInput > * > div, textarea, textarea {
          padding: ${props => props.theme.layout(8)} ${props => props.theme.layout(14)};
          scroll-padding: ${props => props.theme.layout(8)} ${props => props.theme.layout(14)};
        }
        
        &:has(.MuiInputAdornment-positionStart, .MuiInputAdornment-positionEnd) {
          > .EditorInput, > .MentionsInput, > textarea {
            width: calc(100% + (${props => props.theme.layout(14)} * 1));
          }

          &:has(.MuiInputAdornment-positionStart) {
            > .EditorInput, > .MentionsInput, > textarea {
              margin-left: 0;
            }
            > .EditorInput .ProseMirror, > .MentionsInput > * > div, textarea, textarea {
              padding-left: 0;
              scroll-padding-left: 0;
            }
          }

          &:has(.MuiInputAdornment-positionEnd) {
            > .EditorInput, > .MentionsInput, > textarea {
              margin-right: 0;
            }
            > .EditorInput .ProseMirror, > .MentionsInput > * > div, textarea, textarea {
              padding-right: 0;
              scroll-padding-right: 0;
            }
          }
        }
      }
      
      .MuiInputAdornment-root {
        align-items: flex-start;
        max-height: unset;
        margin-top: ${props => props.theme.layout(-1)};
        height: 100%;

        > .Icon {
          margin-top: ${props => props.theme.layout(2)};
        }
        .IconButton {
          margin-top: ${props => props.theme.layout(0)};
        }
      }
    }
  }
  
  //sizes
  &.TextField-size-smaller {
    .MuiInputAdornment-root {
      .Icon {
        font-size: ${props => props.theme.fontSize(20)};
      }
      .MuiTypography-root {
        font-size: inherit;
      }
    }

    .MuiInputBase-root {
      font-size: ${props => props.theme.fontSize(15)};
      line-height: ${props => props.theme.lineHeight(`${22.5 / 15}f`)};
      input::placeholder, textarea::placeholder {
        font-size: ${props => props.theme.fontSize(15)};
        line-height: ${props => props.theme.lineHeight(`${22.5 / 15}f`)};
      }
    }
    
    .MuiInputAdornment-root {
      .MuiButtonBase-root {
        padding: ${props => props.theme.layout(2)};
      }
    }

    &.TextField-multiline {
      .MuiInputBase-root {
        padding: ${props => props.theme.layout(6.5)} ${props => props.theme.layout(12)};

        > .EditorInput, > .MentionsInput, > textarea {
          margin: ${props => props.theme.layout(-6.5)} ${props => props.theme.layout(-12)};
          width: calc(100% + (${props => props.theme.layout(12)} * 2));
          height: calc(100% + (${props => props.theme.layout(6.5)} * 2));
        }

        > .EditorInput .ProseMirror, > .MentionsInput > * > div, textarea, textarea {
          padding: ${props => props.theme.layout(6.5)} ${props => props.theme.layout(12)};
          scroll-padding: ${props => props.theme.layout(6.5)} ${props => props.theme.layout(12)};
        }

        &:has(.MuiInputAdornment-positionStart, .MuiInputAdornment-positionEnd) {
          > .EditorInput, > .MentionsInput, > textarea {
            width: calc(100% + (${props => props.theme.layout(12)} * 1));
          }
        }
      }
    }
    
    &:not(.TextField-multiline) {
      .MuiInputBase-root {
        min-height: ${props => props.theme.layout(36)};
        padding-top: ${props => props.theme.layout(4)};
        padding-bottom: ${props => props.theme.layout(4)};

        .MuiInputBase-input {
          padding-top: ${props => props.theme.layout(3)};
          padding-bottom: ${props => props.theme.layout(3)};
        }

        &:has(.MuiInputAdornment-positionStart) {
          padding-left: ${props => props.theme.layout(12)};

          .MuiInputAdornment-positionStart {
            margin-right: ${props => props.theme.layout(6)};

            &:has(.MuiButtonBase-root) {
              margin-top: ${props => props.theme.layout(0)};
            }
          }
        }

        &:not(:has(.MuiInputAdornment-positionStart)) {
          .MuiInputBase-input {
            padding-left: ${props => props.theme.layout(12)};
          }
        }

        &:has(.MuiInputAdornment-positionEnd) {
          padding-right: ${props => props.theme.layout(12)};

          .MuiInputAdornment-positionEnd {
            margin-left: ${props => props.theme.layout(4)};

            &:has(.MuiButtonBase-root) {
              margin-top: ${props => props.theme.layout(0)};
            }
          }
        }

        &:not(:has(.MuiInputAdornment-positionEnd)) {
          .MuiInputBase-input {
            padding-right: ${props => props.theme.layout(12)};
          }
        }
      }
    }
  }
  
  &.TextField-size-smallest {
    .MuiInputAdornment-root {
      .Icon {
        font-size: ${props => props.theme.fontSize(18)};
      }
      .MuiTypography-root {
        font-size: inherit;
      }
    }

    .MuiInputBase-root {
      font-size: ${props => props.theme.fontSize(14)};
      line-height: ${props => props.theme.lineHeight(`${20 / 14}f`)};
      input::placeholder, textarea::placeholder {
        font-size: ${props => props.theme.fontSize(14)};
        line-height: ${props => props.theme.lineHeight(`${20 / 14}f`)};
      }
    }

    .MuiInputAdornment-root {
      .MuiButtonBase-root {
        padding: ${props => props.theme.layout(2)};
      }
    }

    &.TextField-multiline {
      .MuiInputBase-root {
        padding: ${props => props.theme.layout(6)} ${props => props.theme.layout(10)};

        > .EditorInput, > .MentionsInput, > textarea {
          margin: ${props => props.theme.layout(-6.5)} ${props => props.theme.layout(-10)};
          width: calc(100% + (${props => props.theme.layout(10)} * 2));
          height: calc(100% + (${props => props.theme.layout(6.5)} * 2));
        }

        > .EditorInput .ProseMirror, > .MentionsInput > * > div, textarea, textarea {
          padding: ${props => props.theme.layout(6.5)} ${props => props.theme.layout(10)};
          scroll-padding: ${props => props.theme.layout(6.5)} ${props => props.theme.layout(10)};
        }

        &:has(.MuiInputAdornment-positionStart, .MuiInputAdornment-positionEnd) {
          > .EditorInput, > .MentionsInput, > textarea {
            width: calc(100% + (${props => props.theme.layout(10)} * 1));
          }
        }
      }
    }
    
    &:not(.TextField-multiline) {
      .MuiInputBase-root {
        min-height: ${props => props.theme.layout(32)};
        padding: ${props => props.theme.layout(2)} 0;

        .MuiInputBase-input {
          padding-top: ${props => props.theme.layout(3)};
          padding-bottom: ${props => props.theme.layout(3)};
        }

        &:has(.MuiInputAdornment-positionStart) {
          padding-left: ${props => props.theme.layout(12)};

          .MuiInputAdornment-positionStart {
            margin-right: ${props => props.theme.layout(4)};

            &:has(.MuiButtonBase-root) {
              margin-top: ${props => props.theme.layout(0)};
            }
          }
        }

        &:not(:has(.MuiInputAdornment-positionStart)) {
          .MuiInputBase-input {
            padding-left: ${props => props.theme.layout(10)};
          }
        }

        &:has(.MuiInputAdornment-positionEnd) {
          padding-right: ${props => props.theme.layout(12)};

          .MuiInputAdornment-positionEnd {
            margin-left: ${props => props.theme.layout(4)};

            &:has(.MuiButtonBase-root) {
              margin-top: ${props => props.theme.layout(0)};
            }
          }
        }

        &:not(:has(.MuiInputAdornment-positionEnd)) {
          .MuiInputBase-input {
            padding-right: ${props => props.theme.layout(10)};
          }
        }
      }
    }
  }
  
  &.TextField-size-tiny {
    .MuiInputAdornment-root { 
      .Icon {
        font-size: ${props => props.theme.fontSize(8)};
      }
      .MuiTypography-root {
        font-size: inherit;
      }

      .IconButton {
        > .Icon {
          font-size: ${props => props.theme.fontSize(18)};
        }
      }
    }

    .MuiInputBase-root {
      font-size: ${props => props.theme.fontSize(13)};
      input::placeholder, textarea::placeholder {
        font-size: ${props => props.theme.fontSize(13)};
      }
    }
    
    .MuiInputAdornment-root {
      .MuiButtonBase-root {
        padding: ${props => props.theme.layout(0)};
      }
    }

    &.TextField-multiline {
      .MuiInputBase-root {
        padding: ${props => props.theme.layout(2)} ${props => props.theme.layout(8)};

        > .EditorInput, > .MentionsInput, > textarea {
          margin: ${props => props.theme.layout(-4)} ${props => props.theme.layout(-8)};
          width: calc(100% + (${props => props.theme.layout(8)} * 2));
          height: calc(100% + (${props => props.theme.layout(4)} * 2));
        }

        > .EditorInput .ProseMirror, > .MentionsInput > * > div, textarea, textarea {
          padding: ${props => props.theme.layout(4)} ${props => props.theme.layout(8)};
          scroll-padding: ${props => props.theme.layout(4)} ${props => props.theme.layout(8)};
        }

        &:has(.MuiInputAdornment-positionStart, .MuiInputAdornment-positionEnd) {
          > .EditorInput, > .MentionsInput, > textarea {
            width: calc(100% + (${props => props.theme.layout(8)} * 1));
          }
        }
      }
    }
    
    &:not(.TextField-multiline) {
      .MuiInputBase-root {
        min-height: ${props => props.theme.layout(24)};
        padding: ${props => props.theme.layout(2)} 0;

        .MuiInputBase-input {
          padding-top: ${props => props.theme.layout(0)};
          padding-bottom: ${props => props.theme.layout(0)};
        }

        &:has(.MuiInputAdornment-positionStart) {
          padding-left: ${props => props.theme.layout(6)};

          .MuiInputAdornment-positionStart {
            margin-right: ${props => props.theme.layout(4)};

            &:has(.MuiButtonBase-root) {
              margin-top: ${props => props.theme.layout(0)};
            }
          }
        }

        &:not(:has(.MuiInputAdornment-positionStart)) {
          .MuiInputBase-input {
            padding-left: ${props => props.theme.layout(8)};
          }
        }

        &:has(.MuiInputAdornment-positionEnd) {
          padding-right: ${props => props.theme.layout(6)};

          .MuiInputAdornment-positionEnd {
            margin-left: ${props => props.theme.layout(4)};

            &:has(.MuiButtonBase-root) {
              margin-top: ${props => props.theme.layout(0)};
            }
          }
        }

        &:not(:has(.MuiInputAdornment-positionEnd)) {
          .MuiInputBase-input {
            padding-right: ${props => props.theme.layout(8)};
          }
        }
      }
    }

    .MuiInputAdornment-positionEnd {
      &:not(:has(~ .MuiInputAdornment-positionEnd)) {
        margin-right: ${props => props.theme.layout(2)};
      }
      
      .IconButton {
        margin-right: ${props => props.theme.layout(1)};
      }
    }
  }
  
  ${ComponentStyles}
`;

StyledTextField.propTypes = {
  theme: PropTypes.object
};

export default StyledTextField;
